import type {
	LocationMode,
	RoutingLocationFilters,
	RoutingLocationPolicy,
} from "@/types/admin-portal/routing-mode";

// Backend bounds (RoutingLocationPolicyInput); the backend re-validates and its 400 is
// shown. Speed starts at 1: the backend checks > 0 before rounding to two decimals, so
// 0.001 would pass and be stored as 0.
export const FACTOR_MIN = 1;
export const FACTOR_MAX = 3;
export const SPEED_MIN = 1;
export const SPEED_MAX = 120;

/** Applied in the browser: the backend returns every location in one list. */
export function matches(row: RoutingLocationPolicy, f: RoutingLocationFilters) {
	// An unconfigured location reports mode HERE; the filter calls it DEFAULT.
	const stored = row.configured ? row.mode : "DEFAULT";
	const calibration = row.calibrated ? "CALIBRATED" : "UNCALIBRATED";
	if (f.state && row.state !== f.state) return false;
	if (f.city && row.city !== f.city) return false;
	if (f.mode && stored !== f.mode) return false;
	if (f.calibration && calibration !== f.calibration) return false;
	return true;
}

/** One page of rows, clamped so a list that shrank never strands us past its end. */
export function pageOf<T>(rows: T[], page: number, limit: number) {
	const pages = Math.max(1, Math.ceil(rows.length / limit));
	const current = Math.min(page, pages);
	return {
		pages,
		current,
		rows: rows.slice((current - 1) * limit, current * limit),
	};
}

/**
 * Select all covers the rows on screen: it fills the page, or clears it once all of it
 * is picked. Picks on other pages stay either way.
 */
export function togglePage(selected: number[], pageIds: number[]) {
	const picked = new Set(selected);
	return pageIds.every((id) => picked.has(id))
		? selected.filter((id) => !pageIds.includes(id))
		: [...new Set([...selected, ...pageIds])];
}

/**
 * The edit payload. Merge semantics: only what changed is sent, so an untouched
 * calibration is kept; null clears a field. An unconfigured location reports mode HERE,
 * and an absent mode creates it as HERE.
 */
export function locationChanges(
	location: RoutingLocationPolicy,
	draft: {
		mode: LocationMode;
		factor: number | null;
		speed: number | null;
		notes: string;
	},
) {
	const changes: Record<string, unknown> = {};
	const notes = draft.notes.trim() || null;
	if (draft.mode !== location.mode) changes.mode = draft.mode;
	if (draft.factor !== location.circuityFactor)
		changes.circuity_factor = draft.factor;
	if (draft.speed !== location.speedKmh) changes.speed_kmh = draft.speed;
	if (notes !== location.notes) changes.notes = notes;
	return changes;
}

/** null = empty field = the backend default; NaN (not a number) is always an error. */
export function calibrationErrors(factor: number | null, speed: number | null) {
	return {
		factor: factor !== null && !(factor >= FACTOR_MIN && factor <= FACTOR_MAX),
		speed: speed !== null && !(speed >= SPEED_MIN && speed <= SPEED_MAX),
	};
}
